import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DemoScheduler = ({ approval, onSchedule, onClose }) => {
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    duration: '60',
    meetingLink: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.date) newErrors.date = 'Date is required';
    if (!formData?.time) newErrors.time = 'Time is required';
    if (!formData?.meetingLink?.trim()) newErrors.meetingLink = 'Meeting link is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSchedule({
        approvalId: approval?.id,
        ...formData,
        scheduledAt: new Date()?.toISOString()
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl md:text-2xl font-semibold text-foreground">Schedule Demo</h3>
            <p className="text-sm text-muted-foreground mt-1">{approval?.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Demo Date"
              type="date"
              value={formData?.date || ''}
              onChange={(e) => handleChange('date', e?.target?.value)}
              error={errors?.date}
              required
              min={new Date()?.toISOString()?.split('T')?.[0]}
            />
            <Input
              label="Demo Time"
              type="time"
              value={formData?.time || ''}
              onChange={(e) => handleChange('time', e?.target?.value)}
              error={errors?.time}
              required
            />
          </div>

          <Select
            label="Duration"
            options={durationOptions}
            value={formData?.duration || '60'}
            onChange={(value) => handleChange('duration', value)}
          />

          <Input
            label="Meeting Link"
            type="url"
            placeholder="https://meet.google.com/xxx or https://zoom.us/j/xxx"
            description="Zoom, Google Meet, or Microsoft Teams link"
            value={formData?.meetingLink || ''}
            onChange={(e) => handleChange('meetingLink', e?.target?.value)}
            error={errors?.meetingLink}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Preparation Notes
              <span className="text-muted-foreground ml-1">(Optional)</span>
            </label>
            <textarea
              className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
              rows={4}
              placeholder="Add any notes about what to prepare or discuss during the demo..."
              value={formData?.notes || ''}
              onChange={(e) => handleChange('notes', e?.target?.value)}
            />
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="Calendar" size={20} className="text-primary mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-foreground mb-1">Demo Summary</h4>
                <div className="space-y-1 text-sm text-muted-foreground">
                  {formData?.date && formData?.time && (
                    <p>
                      <Icon name="Clock" size={14} className="inline mr-1" />
                      {new Date(`${formData?.date}T${formData?.time}`)?.toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} at {formData?.time}
                    </p>
                  )}
                  <p>
                    <Icon name="Timer" size={14} className="inline mr-1" />
                    Duration: {formData?.duration} minutes
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSubmit} iconName="Calendar" iconPosition="left" className="flex-1">
            Schedule Demo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DemoScheduler;